import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SolutionsPageTemplate from "../../components/SolutionsPageTemplate/SolutionsPageTemplate";

const SEO_TITLE = "Project Based Team Productivity and Performance Software | TeamGrid";
const SEO_DESCRIPTION = "Improve project team productivity with workload tracking, proactive work insights and performance analytics without burnout.";
const SEO_CANONICAL = "https://teamgrid.ai/solutions/project-based-teams";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const ProjectBasedTeams = () => {
  const navigate = useNavigate();
  // FAQ data specific to Project-Based Teams
  const projectTeamsFAQs = [
    {
      question: "How does TeamGrid help with project bottleneck visibility?",
      answer:
        "TeamGrid reveals slow work stages and dependencies, giving project teams insights into delivery bottlenecks and workflow delays.",
    },
    {
      question: "Can TeamGrid improve on-time delivery rates?",
      answer:
        "Yes, by improving work visibility and predicting risk zones, TeamGrid helps project teams deliver on schedule.",
    },
    {
      question: "Does TeamGrid replace traditional project management tools?",
      answer:
        "No, TeamGrid complements PM tools by adding productivity analytics and work pattern visibility.",
    },
  ];

  const customButton = (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate("/book-demo")}
      className="flex items-center justify-center px-7 py-3.5 rounded-[14px] font-semibold text-black transition-all"
      style={{
        background: "#00D1C1",
        boxShadow: "0px 18px 40px rgba(0, 209, 193, 0.45)",
      }}
    >
      Get a Demo
    </motion.button>
  );

  return (
    <>
      <Helmet>
        <title>{SEO_TITLE}</title>
        <meta name="description" content={SEO_DESCRIPTION} />
        <link rel="canonical" href={SEO_CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SEO_CANONICAL} />
        <meta property="og:title" content={SEO_TITLE} />
        <meta property="og:description" content={SEO_DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={SEO_CANONICAL} />
        <meta name="twitter:title" content={SEO_TITLE} />
        <meta name="twitter:description" content={SEO_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "How does TeamGrid help with project bottleneck visibility?", acceptedAnswer: { "@type": "Answer", text: "TeamGrid identifies slow work stages and task dependencies, giving project teams visibility into delivery bottlenecks and workflow delays." } },
            { "@type": "Question", name: "Can TeamGrid improve on-time delivery rates?", acceptedAnswer: { "@type": "Answer", text: "Yes, by improving work visibility and highlighting potential risk areas, TeamGrid helps project teams improve on-time delivery." } },
            { "@type": "Question", name: "Does TeamGrid replace traditional project management tools?", acceptedAnswer: { "@type": "Answer", text: "No, TeamGrid complements existing project management tools by adding productivity analytics and work pattern insights." } },
          ],
        })}</script>
      </Helmet>
      <SolutionsPageTemplate
        breadcrumbTitle="Project-Based Teams"
      heroTitle="Project Delivery Performance & <span style='color: #00D1C1; font-weight: 600;'>Resource Visibility Software</span>"
      heroSubtitle="Deliver Projects On Time with Clear Resource & Progress Visibility"
      primaryButtonText="Get a Demo for Your Project Team"
      customPrimaryButton={customButton}
      fullSolutionContentTitle="Improve Project Delivery & Resource Utilization"
      fullSolutionContentDescription="TeamGrid helps project teams improve delivery timelines, balance workloads, and control budgets with confidence through real-time resource allocation tracking, project health monitoring, deadline risk assessment, and cross-project dependency management."
      fullSolutionContentOutcomes="Key outcomes include 35% improvement in on-time project delivery, 28% better resource utilization, 32% reduction in budget overruns, and 25% improvement in project margins."
      hideProductivity={true}
      hideFeatures={true}
      hideOutcomes={true}
      hideWhyChoose={true}
        faqs={projectTeamsFAQs}
        faqSubtitle="Everything you need to know about TeamGrid for project-based teams."
      />
    </>
  );
};

export default ProjectBasedTeams;
